import IconSeo from '@assets/images/icons/seo-new.svg?react'
import { Loading, Modal } from '@lib/components'
import { IconArrowDownLine } from '@lib/icons'
import { Link as LinkIcon } from '@octopus-do/octopus-storybook/icons'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { openSeoTags } from '@storeOld/reducers/tree'
import { closeModal } from '@storeOld/reducers/ui'
import type React from 'react'
import { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { GtmTriggerName, sendGtmEvent } from '@/constants/gtm'
import { usePdf, useProjectAccess } from '@/hooks'
import { PAGE_PERMISSION } from '@/permissions/project/definitions'
import { GlobalActions, selectIsLoading } from '@/store'
import type { ModalProps } from '@/types'
import { PageContent } from '../PageContent'

type Props = {
  nodeId: string
  onClose: () => void
}

interface IconProps {
  isFetching: boolean
}

const ExportIcon: React.FC<IconProps> = ({ isFetching }) => {
  if (isFetching) {
    return <Loading color="gray" type="small" variant="two" />
  }
  return <IconArrowDownLine size={16} />
}

export const PageContentView: React.FC<ModalProps & Props> = ({
  onClose,
  nodeId,
}) => {
  const dispatch = useDispatch()

  const { hasNotAvailableAccess } = useProjectAccess(PAGE_PERMISSION.EXPORT_PDF)

  const activeTabId = useSelector(
    migrationProjectSelector.getActiveOrFirstTabId
  )
  const projectId = useSelector(migrationProjectSelector.getProjectId)

  const { submit } = usePdf(
    'export_pdf',
    undefined,
    onClose,
    onClose,
    `/export/${projectId}/page/${nodeId}`
  )

  const isFetching = useSelector(selectIsLoading('export_pdf'))

  const onExport = useCallback(async () => {
    sendGtmEvent(GtmTriggerName.CONTENT_EXPORT_PDF)
    return submit()
  }, [])

  const onCopy = useCallback(() => {
    const { origin } = window.location

    dispatch(
      GlobalActions.copy({
        value: `${origin}/${projectId}/${activeTabId}/p/${nodeId}/content`,
      })
    )
  }, [])

  const handleOpenSeoTagsModal = () => {
    // @todo implement action
    // @ts-expect-error
    dispatch(openSeoTags(nodeId))
    dispatch(closeModal())
  }

  // biome-ignore lint/plugin/no-jsx-in-function-variables: legacy pattern
  const moreButtons = useMemo(
    () => [
      [
        {
          icon: <ExportIcon isFetching={isFetching} />,
          onClick: onExport,
          disabled: isFetching || hasNotAvailableAccess,
          text: 'PDF',
        },
        {
          icon: <LinkIcon />,
          onClick: onCopy,
          disabled: !projectId,
          text: 'Copy link',
        },
        {
          icon: <IconSeo />,
          onClick: handleOpenSeoTagsModal,
          text: 'SEO Tags',
        },
      ],
    ],
    [
      handleOpenSeoTagsModal,
      onCopy,
      onExport,
      isFetching,
      hasNotAvailableAccess,
    ]
  )

  return (
    <Modal onClose={onClose} moreButtons={moreButtons} withSyncStatus>
      <PageContent nodeId={nodeId} withComments />
    </Modal>
  )
}
